/**
 * Helper functions for creating DOM elements (generic, not picker-specific)
 */

/**
 * Create a button element with jQuery
 * Helper function for creating buttons consistently
 * @param {Object} config - Configuration object
 * @param {string} config.text - Button text
 * @param {string} config.className - CSS class name
 * @param {Function} config.onClick - Click handler
 * @returns {jQuery} jQuery button element
 */
export const createPickerButton = ({ text, className = '', onClick }) => {
  /* global $ */
  const $button = $('<button />', {
    class: className,
    text,
    tabindex: '-1',
    type: 'button',
  });

  if (onClick) {
    $button.on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    });
  }

  return $button;
};

/**
 * Creates a single event badge element
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.type - Event type
 * @param {string} config.label - Event label
 * @param {Function} config.getEmoji - Function to get emoji for event type (optional)
 * @returns {jQuery} jQuery element for the badge
 */
export const createEventBadge = ({ type, label, getEmoji }) => {
  /* global $ */
  // Get emoji for this event type (optional)
  const emoji = getEmoji ? getEmoji(type) : '';
  
  // Create badge text with emoji if available
  const badgeText = emoji ? `${emoji} ${label}` : label;
  
  return $('<span />', {
    class: 'calendar-event-badge',
    text: badgeText,
    css: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: '12px',
      fontStyle: 'italic',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: '#000000',
      color: '#ffffff',
      border: '1px solid #000000',
    },
    attr: {
      'aria-label': `Event: ${label}`,
    },
  });
};

/**
 * Creates a container with all event badges
 * 
 * @param {Array} badges - Array of badge objects with type and label properties
 * @param {Function} createBadgeFn - Function to create a single badge (optional, defaults to createEventBadge)
 * @returns {jQuery} jQuery element for the badges container
 */
export const createBadgesContainer = (badges, createBadgeFn) => {
  /* global $ */
  const $badgesContainer = $('<div />', {
    class: 'calendar-event-badges-container',
    css: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
  });

  // Use provided function or default to createEventBadge
  const createBadge = createBadgeFn || createEventBadge;

  // Create a badge for each event type
  badges.forEach((badge) => {
    const $badge = createBadge(badge);
    $badgesContainer.append($badge);
  });

  return $badgesContainer;
};

/**
 * Creates an event emoji element
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.emoji - Emoji string to display
 * @returns {jQuery} jQuery element for the emoji
 */
export const createEventEmoji = ({ emoji }) => {
  /* global $ */
  return $('<span />', {
    class: 'calendar-event-emoji',
    text: emoji,
    css: {
      display: 'block',
      fontSize: '14px',
      lineHeight: '1',
      marginBottom: '2px',
      textAlign: 'center',
    },
  });
};


